import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import {
  AddDriverDetailsRequest,
  AddDriverDetailsResponse,
  CheckDriverEmailResponse,
  Driver,
  RegistrationDriver,
} from "../../types/driverTypes"
import {
  addDriverDetails,
  checkDriverEmail,
  completeAddDriver,
  getDrivers,
  getDriverTokens,
  uploadDocument,
  disconnectDriver,
} from "../api/driverAPI"
import { getMemberId } from "../../utils/utils"
import { RootState } from "../store"
import sha1 from "sha1"

export type DriverState = {
  driversList: Driver[]
  registrationDriver?: RegistrationDriver | undefined,
  disconnectDriverLoading: boolean,
  disconnectDriverSuccess: boolean,
}

const initialState: DriverState = {
  driversList: [],
  disconnectDriverLoading: false,
  disconnectDriverSuccess: false,
}

export const getDriversAsync = createAsyncThunk<Driver[], void>(
  "driver/getDrivers",
  async (_, { getState }: any) => {
    const { userData } = getState().auth
    const memberId = getMemberId(userData?.personal.customerToken)
    const response: Driver[] = await getDrivers(memberId)
    return response
  },
)

export const checkDriverEmailAsync = createAsyncThunk<
  CheckDriverEmailResponse,
  string
>("driver/checkDriverEmail", async (email, { getState }: any) => {
  const { userData } = getState().auth
  const memberId = getMemberId(userData?.personal.customerToken)
  const response: CheckDriverEmailResponse = await checkDriverEmail(
    email,
    memberId,
  )
  if (response.value === "true") {
    return response
  } else if (response.value === "false") {
    return response
  } else {
    throw new Error("checkDriverEmailAsync failed")
  }
})

export const addDriverDetailsAsync = createAsyncThunk<
  AddDriverDetailsResponse,
  AddDriverDetailsRequest
>(
  "driver/addDriverDetails",
  async ({ email, firstName, lastName, phone }, { getState }: any) => {
    const { userData } = getState().auth
    const memberId = getMemberId(userData?.personal.customerToken)
    const response: AddDriverDetailsResponse = await addDriverDetails(
      email,
      firstName,
      lastName,
      phone,
      memberId,
    )
    if (response.value === "true") {
      return response
    } else {
      throw new Error("addDriverDetails failed")
    }
  },
)

export const getRegisteredDriverAsync = createAsyncThunk<
  RegistrationDriver,
  string
>("driver/getDriverTokens", async (token, { getState, dispatch }: any) => {
  const response: RegistrationDriver = await getDriverTokens(token)
  return response
})

export const uploadDocumentAsync = createAsyncThunk<any, any>(
  "driver/uploadDocument",
  async ({ base64, documentType }, { getState }: any) => {
    const { registrationDriver } = getState().driver
    const base64Data = base64.replace(/^data:image\/[a-z]+;base64,/, "")

    const reqData = {
      data: base64Data,
      mineType: "image/png",
      documentType,
    }
    const response: any = await uploadDocument(
      reqData,
      registrationDriver.token,
      registrationDriver.customerToken,
    )
    return response
  },
)

export const completeAddDriverAsync = createAsyncThunk<any, any>(
  "driver/completeAddDriver",
  async ({ formData, images }, { getState }: any) => {
    const registrationDriver = getState().driver.registrationDriver

    const reqData = {
      agreeToContract: formData.acknowledgesTerms,
      agreeToPrivacyPolicy: formData.agreesToPrivacyPolicy,
      agreeToNewsletter: formData.wantsSpecialOffers,
      isYoungDriver: formData.under23AndInexperienced,
      idImageSideA: images[2].payload,
      idImageSideB: images[3].payload,
      driverLicenseSideA: images[0].payload,
      driverLicenseSideB: images[1].payload,
      address: formData.street,
      city: formData.city,
      apartmentNumber: formData.street,
      postalCode: formData.postalCode,
      password: sha1(formData.password),
      pincode: formData.pinCode,
    }
    const response: any = await completeAddDriver(
      reqData,
      registrationDriver.token,
      registrationDriver.customerToken,
    )
    if (response.value === "true") {
      return response
    } else {
      throw new Error("completeAddDriver failed")
    }
  },
)

export const disconnectDriverAsync = createAsyncThunk<any, any>(
  "driver/disconnectDriver",
  async ({ driverId, disconnect }, { getState }: any) => {
    
    const customerToken = getState().auth.userData.personal.customerToken;
    const loginToken = localStorage.getItem("x-Token") as string;


    const reqData = {
      driverId,
      disconnect,
    };

    const response: any = await disconnectDriver(
      reqData,
      loginToken,
      customerToken,
    );

    return response
  },
)

export const driverSlice = createSlice({
  name: "driver",
  initialState,
  reducers: {
    setDrivers: (state, action: PayloadAction<Driver[]>) => {
      state.driversList = action.payload
    },
    setDisconnectDriverSuccess: (state) => {
      state.disconnectDriverSuccess = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRegisteredDriverAsync.pending, (state) => {})
      .addCase(
        getRegisteredDriverAsync.fulfilled,
        (state, action: PayloadAction<RegistrationDriver>) => {
          state.registrationDriver = action.payload
        },
      )
      .addCase(getRegisteredDriverAsync.rejected, (state) => {})
      .addCase(getDriversAsync.pending, (state) => {})
      .addCase(
        getDriversAsync.fulfilled,
        (state, action: PayloadAction<Driver[]>) => {
          state.driversList = action.payload
        },
      )
      .addCase(getDriversAsync.rejected, (state) => {})
      .addCase(disconnectDriverAsync.pending, (state) => {
        state.disconnectDriverLoading = true;
      })
      .addCase(disconnectDriverAsync.fulfilled, (state) => {
        state.disconnectDriverLoading = false;
        state.disconnectDriverSuccess = true;
      })
      .addCase(disconnectDriverAsync.rejected, (state) => {
        state.disconnectDriverLoading = false;
        state.disconnectDriverSuccess = false;
      })
  },
})

export const { setDisconnectDriverSuccess } = driverSlice.actions

export const selectDriversList = (state: RootState) => state.driver.driversList
export const selectRegistrationDrivrData = (state: RootState) =>
  state.driver.registrationDriver

export default driverSlice.reducer
